import React, { useContext, useMemo, useState } from 'react';
import styled from 'styled-components';
import moment from 'moment';
import { Button, FlexColumn, FlexMiddle, Loader } from '@ubeya/shared-web/components';
import useEmployeeContracts from '@ubeya/shared/hooks/employees/useEmployeeContracts';
import useEmployeePto from '@ubeya/shared/hooks/employees/useEmployeePto';
import { AccordionSummaryContainer as BaseAccordionSummaryContainer } from '@ubeya/shared-web/components/Accordion/Accordion';
import ConfigContext from '@ubeya/shared-web/contexts/ConfigContext';
import PrivilegesContext from '@ubeya/shared-web/contexts/PrivilegesContext';
import useFiscalPeriod from '@ubeya/shared/hooks/account/useFiscalPeriod';
import useFormattingMap from '@ubeya/shared-web/hooks/useFormattingMap';
import withPermissions from '@ubeya/shared-web/hoc/withPermissions';
import { API_DATE_FORMAT } from '@ubeya/shared/constants';
import privileges from '@ubeya/shared-web/privileges';
import { RootWrapper } from '../styled';
import ContractEmptyState from '../EmployeeContracts/ContractEmptyState';
import EmployeeBalance from './EmployeeBalance';
import PtoBalanceHistory from './PtoBalanceHistory';
import AdjustPtoAllowanceBalanceModal from './AdjustPtoAllowanceBalanceModal';
import UseAllPtoBalanceModal from './UseAllPtoBalanceModal';

const Wrapper = styled(FlexColumn)`
  flex: 1;
  padding: 0 41px 40px;
  min-width: 500px;
  gap: 16px;
`;

const HeaderContent = styled(FlexColumn)`
  font-size: 16px;
  line-height: 24px;
  font-weight: 500;
`;

const DatesContainer = styled.div`
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
`;

const AccordionSummaryContainer = styled(BaseAccordionSummaryContainer)`
  height: auto;
`;

const ActionButton = styled(Button)`
  background: transparent;
  padding: 7px 8px;
  line-height: 20px;
  font-weight: 400;
  display: flex;
  align-items: center;

  &:hover,
  &:focus {
    background: transparent !important;
  }
`;

const DangerActionButton = styled(Button)`
  padding: 7px 8px;
  line-height: 20px;
  font-weight: 400;
  display: flex;
  align-items: center;
  background-color: ${({ theme }) => theme.colors.redNegative};
  border-color: ${({ theme }) => theme.colors.redNegative};
  color: ${({ theme }) => theme.colors.white};

  &:hover,
  &:focus {
    background: ${({ theme }) => theme.colors.redLight} !important;
    border-color: ${({ theme }) => theme.colors.redLight} !important;
    color: ${({ theme }) => theme.colors.white} !important;
  }
`;

const ButtonsContainer = styled(FlexMiddle)`
  gap: 8px;
`;

const LeaveManagement = ({ data: employeeData, userPermissions: { hasActionPermission } }) => {
  const { t } = useContext(ConfigContext);
  const formattingMap = useFormattingMap();
  const myPrivileges = useContext(PrivilegesContext);
  const [showAdjustBalanceModal, setShowAdjustBalanceModal] = useState(false);
  const [showUseAllPtoModal, setShowUseAllPtoModal] = useState(false);
  const { id: employeeId, firstName, lastName, branches = [] } = employeeData || {};

  const hasEditContractsPrivilege = !!myPrivileges[privileges.EDIT_CONTRACT_TYPE];

  const { data: contracts, isLoading: loadingEmployeeContracts } = useEmployeeContracts({
    employeeId
  });
  const { activeFiscalPeriod } = useFiscalPeriod();

  const activeContract = useMemo(() => {
    return (contracts || []).find((contract) => contract.isActive);
  }, [contracts]);

  const eligibleStartDate = useMemo(() => {
    if (!activeContract) return null;
    const { startDate } = activeContract;
    const { startDate: fiscalStartDate } = activeFiscalPeriod || {};

    if (!startDate && !fiscalStartDate) return null;

    if (!startDate) return fiscalStartDate;
    if (!fiscalStartDate) return startDate;

    const contractStartDateMoment = startDate && moment.utc(startDate);
    const fiscalStartDateMoment = fiscalStartDate && moment.utc(fiscalStartDate);
    const maxMoment = moment.max(contractStartDateMoment, fiscalStartDateMoment);
    return maxMoment.format(API_DATE_FORMAT);
  }, [activeFiscalPeriod, activeContract]);

  const { data, isLoading, addPtoDays, addPtoBalanceAdjustment, deletePto, employeeUseAllPtoBalance } = useEmployeePto({
    employeeId,
    employeeContractId: activeContract?.id
  });

  const noActiveContract = !activeContract && !loadingEmployeeContracts;
  const employeeName = `${firstName || ''} ${lastName || ''}`.trim();
  const currentBalance = data?.balance?.holidayAccrualBalance;
  const hasBalanceFeature = currentBalance !== null && currentBalance !== undefined;
  const hasFutureJobs = data?.hasFutureJobs ?? false;

  return (
    <RootWrapper>
      <Wrapper>
        <AccordionSummaryContainer>
          <HeaderContent>
            {activeFiscalPeriod ? (
              <>
                {t('holidayAccrualBalance')}
                <DatesContainer>{`(${formattingMap.startAndEndDates(
                  activeFiscalPeriod.startDate,
                  activeFiscalPeriod.endDate
                )})`}</DatesContainer>
              </>
            ) : (
              t('holidayAccrualBalance')
            )}
          </HeaderContent>
          {!noActiveContract && hasActionPermission && (
            <ButtonsContainer>
              {hasEditContractsPrivilege && hasBalanceFeature && currentBalance !== 0 && (
                <DangerActionButton secondary type="button" onClick={() => setShowUseAllPtoModal(true)}>
                  {t('useAllPtoButton')}
                </DangerActionButton>
              )}
              <ActionButton secondary type="button" onClick={() => setShowAdjustBalanceModal(true)}>
                {t('contractTypes_adjustAllowanceBalance')}
              </ActionButton>
            </ButtonsContainer>
          )}
        </AccordionSummaryContainer>

        {isLoading ? (
          <Loader />
        ) : noActiveContract ? (
          <ContractEmptyState />
        ) : (
          <>
            {activeContract && (
              <>
                <EmployeeBalance balance={data?.balance} activeContract={activeContract} />
                <PtoBalanceHistory
                  ptoData={data}
                  addPtoDays={addPtoDays}
                  deletePto={deletePto}
                  employeeBranches={branches}
                  eligibleStartDate={eligibleStartDate}
                  activeContract={activeContract}
                />
              </>
            )}
          </>
        )}
      </Wrapper>

      {showAdjustBalanceModal && (
        <AdjustPtoAllowanceBalanceModal
          onClose={() => setShowAdjustBalanceModal(false)}
          addPtoBalanceAdjustment={addPtoBalanceAdjustment}
          activeContract={activeContract}
        />
      )}

      {showUseAllPtoModal && (
        <UseAllPtoBalanceModal
          onClose={() => setShowUseAllPtoModal(false)}
          employeeUseAllPtoBalance={employeeUseAllPtoBalance}
          employeeBranches={branches}
          employeeName={employeeName}
          currentBalance={currentBalance}
          hasFutureJobs={hasFutureJobs}
        />
      )}
    </RootWrapper>
  );
};

export default withPermissions(React.memo(LeaveManagement), {
  action: { privileges: [privileges.MANAGE_EMPLOYEE_HOLIDAY_ACCRUAL] }
});
